import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

interface KPICardProps {
  title: string;
  value: string;
  subtitle?: string;
  trend?: number; // percentage change
  trendLabel?: string;
  status?: "green" | "amber" | "red" | "neutral";
  icon?: React.ReactNode;
  href?: string;
}

export function KPICard({
  title,
  value,
  subtitle,
  trend,
  trendLabel,
  status = "neutral",
  icon,
  href,
}: KPICardProps) {
  const statusColors = {
    green: "border-l-emerald-500",
    amber: "border-l-amber-500",
    red: "border-l-red-500",
    neutral: "border-l-border",
  };

  const trendColors = {
    up: "text-emerald-500",
    down: "text-red-500",
    flat: "text-muted-foreground",
  };

  const trendDirection =
    trend === undefined ? "flat" : trend > 0 ? "up" : trend < 0 ? "down" : "flat";
  const TrendIcon =
    trendDirection === "up"
      ? TrendingUp
      : trendDirection === "down"
        ? TrendingDown
        : Minus;

  const cardClass = cn(
    "rounded-xl border border-border bg-card p-5 border-l-4 transition-shadow hover:shadow-md",
    statusColors[status],
    href && "cursor-pointer hover:border-primary/30"
  );

  const content = (
    <>
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-card-foreground">{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {icon && (
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            {icon}
          </div>
        )}
      </div>

      {trend !== undefined && (
        <div className={cn("mt-3 flex items-center gap-1 text-xs", trendColors[trendDirection])}>
          <TrendIcon className="h-3 w-3" />
          <span>
            {trend > 0 ? "+" : ""}
            {trend.toFixed(1)}%
          </span>
          {trendLabel && (
            <span className="text-muted-foreground ml-1">{trendLabel}</span>
          )}
        </div>
      )}
    </>
  );

  if (href) {
    return (
      <Link to={href} className={cardClass}>
        {content}
      </Link>
    );
  }

  return <div className={cardClass}>{content}</div>;
}
